/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The e-mail the agency sends Chaves na Mão support to switch on the XML feed
 * or the lead webhook: recipient, subject and message ready to copy, plus a
 * button that opens it in the e-mail app. The link it carries is shown only
 * once, so the modal opens right after it is generated.
 */

import { Anchor, Modal, Text } from '@/shared/ui';
import { CopyButton } from './widgets';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const CNM_SUPPORT_EMAIL = 'atendimento@chavesnamao.com.br';

export type CnmEmailKind = 'feed' | 'leads';

export interface CnmEmail {
  /** The agency's Chaves na Mão account e-mail: the support team finds the account by it. */
  from: string;
  to: string;
  subject: string;
  body: string;
}

/**
 * The e-mail to send Chaves na Mão for ``kind``.
 *
 * @param kind - ``feed`` (XML integration) or ``leads`` (lead webhook).
 * @param url - The feed or webhook URL it carries.
 * @param agency - The agency's name, CPF/CNPJ and Chaves na Mão account e-mail ("" when unknown).
 * @param t - Translation function.
 * @returns Recipient, subject and body.
 */
export function cnmEmail(
  kind: CnmEmailKind, url: string, agency: { name: string; document: string; account: string }, t: Translate,
): CnmEmail {
  const values = {
    url,
    agency: agency.name || t('chavesNaMao.email.semNome'),
    document: agency.document || t('chavesNaMao.email.semDocumento'),
    account: agency.account || t('chavesNaMao.email.semConta'),
  };
  return {
    from: values.account,
    to: CNM_SUPPORT_EMAIL,
    subject: t(`chavesNaMao.email.${kind}.assunto`, values),
    body: t(`chavesNaMao.email.${kind}.corpo`, values),
  };
}

/** ``mailto:`` link for an e-mail. */
export function mailtoHref(email: CnmEmail): string {
  return `mailto:${email.to}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
}

export interface ChavesNaMaoEmailModalProps {
  opened: boolean;
  onClose: () => void;
  kind: CnmEmailKind;
  email: CnmEmail;
  t: Translate;
}

/**
 * Recipient, subject and message, each with its copy button.
 *
 * @param props - Open state, the e-mail and translations.
 * @returns The modal.
 */
export function ChavesNaMaoEmailModal({ opened, onClose, kind, email, t }: ChavesNaMaoEmailModalProps) {
  const copy = { label: t('portal.copyButton.copiar'), copiedLabel: t('portal.copyButton.copiado') };
  return (
    <Modal opened={opened} onClose={onClose} title={t(`chavesNaMao.email.${kind}.titulo`)} size="lg">
      <div className="flex flex-col gap-(--space-4)" data-testid={`chaves-na-mao-email-${kind}`}>
        <Text
          className="m-0 text-xs leading-relaxed p-(--space-3) rounded-(--radius-md) bg-[var(--status-warning-bg)] text-(--text-primary)"
          data-testid="cnm-email-sender"
        >
          {t('chavesNaMao.email.enviarDe', { account: email.from })}
        </Text>
        <Text className="m-0 text-xs text-(--text-secondary) leading-relaxed">{t('chavesNaMao.email.instrucao')}</Text>
        <Field label={t('chavesNaMao.email.para')} value={email.to} testId="cnm-email-to" copy={copy} />
        <Field label={t('chavesNaMao.email.assunto')} value={email.subject} testId="cnm-email-subject" copy={copy} />
        <Field label={t('chavesNaMao.email.mensagem')} value={email.body} testId="cnm-email-body" copy={copy} multiline />
        <Anchor
          href={mailtoHref(email)}
          data-testid="cnm-email-open"
          className="self-start text-xs font-semibold text-[var(--seed-blue)] hover:underline"
        >
          {t('chavesNaMao.email.abrirApp')}
        </Anchor>
      </div>
    </Modal>
  );
}

function Field({ label, value, testId, copy, multiline = false }: {
  label: string; value: string; testId: string; copy: { label: string; copiedLabel: string }; multiline?: boolean;
}) {
  return (
    <div className="flex flex-col gap-(--space-1)">
      <div className="flex items-center justify-between gap-(--space-2)">
        <Text component="span" className="text-xs font-semibold text-(--text-primary)">{label}</Text>
        <CopyButton text={value} label={copy.label} copiedLabel={copy.copiedLabel} />
      </div>
      <Text
        component={multiline ? 'pre' : 'span'}
        data-testid={testId}
        className="m-0 text-xs font-mono whitespace-pre-wrap break-all px-(--space-2) py-(--space-1-5) rounded-(--radius-sm) bg-[var(--surface-secondary)] text-(--text-primary)"
      >
        {value}
      </Text>
    </div>
  );
}
